import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Button } from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import YouTubeIcon from '@mui/icons-material/YouTube';
import { ShimmerTable } from 'react-shimmer-effects';
import AddIcon from '@mui/icons-material/Add';
import { RootState, AppDispatch } from '../../Redux/store';
import MainLayout from '../../components/MainLayout';
import {
  resetPolicyDetails,
  updateOpen,
  updatePolicyState,
} from '../../Redux/Slices/Policy/policy';
import {
  deletePolicy,
  getAllPolicies,
} from '../../Redux/Slices/Policy/policyHandler';
import AddNewPolicy from './AddNewPolicy';
import { SubTitle } from '../../styles/subtitle';
import { ResponsiveHeading } from '../../styles/heading';
import { buttonIconStyles } from '../../styles/icon';
import { SearchBar } from '../../styles/textField';
import { handleSearch } from '../../utils/Groups/groupUtility';
import { SubLabel } from '../../styles/sublabel';
import CommonTable from '../../components/Table/CommonTable';
import GuideButton from '../../components/Buttons/GuideButtons';
import { cyanBlueBackgroundButtonStyles } from '../../styles/button';
import { SmallHeading } from '../../styles/smallHeading';

function PolicyView() {
  const dispatch = useDispatch<AppDispatch>();
  const { allPolicies, status } = useSelector(
    (state: RootState) => state.policySettings,
  );

  const { currentActiveTenantName } = useSelector(
    (state: RootState) => state.customer,
  );
  const [filteredPolicy, setFilteredPolicy] = useState(allPolicies);

  useEffect(() => {
    if (currentActiveTenantName !== '') {
      dispatch(getAllPolicies());
    }
  }, [currentActiveTenantName]);

  useEffect(() => {
    setFilteredPolicy(allPolicies);
  }, [allPolicies]);

  const handleEditPolicy = (id: any) => {
    allPolicies.forEach(async (element: any) => {
      if (element._id === id) {
        dispatch(updatePolicyState(element));
        dispatch(updateOpen(true));
      }
    });
  };
  const handleDeletePolicy = (policyId: string) => {
    dispatch(deletePolicy(policyId));
  };

  const configureNewPolicy = async () => {
    dispatch(resetPolicyDetails());
    dispatch(updateOpen(true));
  };

  const styles = {
    height: { xxl: '33.438rem', xl: '22.188rem', md: '18.75rem' },
  };

  const columnsToSearch = ['identifier', 'description'];

  return (
    <MainLayout>
      <Box
        p={{ md: '0.5rem', lg: '2rem', xl: '2rem' }}
        sx={{ overflowY: 'scroll', height: '95vh' }}
      >
        <Box display="flex" flexDirection="column" gap="0.5rem">
          <ResponsiveHeading>Policy Settings</ResponsiveHeading>
          <SubTitle>Configure Policies for your applications here.</SubTitle>
        </Box>

        <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
          <AddNewPolicy />

          <Box mt={{ md: '0.5rem', lg: '2.5rem', xl: '2.5rem' }} display="flex">
            <Box
              display="flex"
              flexDirection="column"
              p="1.5rem"
              border="0.063rem solid #E3E3E3"
              borderRadius="0.625rem"
              width="70%"
              height="auto"
            >
              <Box display="flex" justifyContent="space-between" mb={2}>
                <SearchBar
                  placeholder="Search for policies"
                  onChange={(event: any) => {
                    handleSearch(
                      event,
                      allPolicies,
                      setFilteredPolicy,
                      columnsToSearch,
                    );
                  }}
                />
                <Button
                  sx={{
                    ...cyanBlueBackgroundButtonStyles,
                    width: { xl: '8rem', md: '6rem' },
                    height: { md: '2.4rem' },
                  }}
                  onClick={configureNewPolicy}
                >
                  <AddIcon sx={{ ...buttonIconStyles, color: 'white' }} />
                  &nbsp; Add New
                </Button>
              </Box>

              {status === 'idle' && <ShimmerTable row={5} col={4} />}
              {status === 'succeeded' && (
                <Box
                  display="flex"
                  flexDirection="column"
                  flexGrow={1}
                  overflow="hidden"
                >
                  <CommonTable
                    data={filteredPolicy}
                    columns={[
                      { field: 'identifier', headerName: 'Identifiers' },
                      { field: 'description', headerName: 'Description' },
                    ]}
                    styles={styles}
                    onEdit={handleEditPolicy}
                    onDelete={handleDeletePolicy}
                    pages="Policy"
                  />
                </Box>
              )}
            </Box>

            <Box
              display="flex"
              flexDirection="column"
              width="30%"
              py="1.5rem"
              px={{ md: '1rem', lg: '2.5rem', xl: '2.5rem' }}
              gap="1rem"
            >
              <SmallHeading variant="button">Note</SmallHeading>
              <SubLabel
                sx={{
                  color: '#777777',
                  fontSize: {
                    xxl: '1.0rem !important',
                    md: '0.8rem !important',
                  },
                }}
              >
                Our system offers Network-Based, Time-Based, and Usability
                Restrictions. These features control access based on network
                parameters, specific time frames, and user roles and
                functionality.
              </SubLabel>
              <GuideButton
                links={['', '']}
                icons={[
                  <YouTubeIcon sx={{ ...buttonIconStyles, color: 'red' }} />,
                  <ArticleIcon sx={{ ...buttonIconStyles, color: 'blue' }} />,
                ]}
                labels={['Video Guide', 'Setup Guide']}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </MainLayout>
  );
}

export default PolicyView;
